import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tabs, Spin, Checkbox, notification as antNotif } from "antd";
import { BellOutlined, AlertOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "util/services";
import { useTranslation } from "react-i18next";
import Alerts from "pages/Alerts";
import styles from "./style.module.scss";
import {
  extractNotificationLink,
  extractNotificationDisplay,
  categoryForTypeId,
} from "components/Notifcation/notificationUtils";
import {
  noticeNotificationQueryKey,
  fetchNoticeNotificationAll,
  deriveFullPageNoticeState,
} from "queries/noticeNotifications";

const NotificationsPanel = ({ user, isDark }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const uid = user?.uid;

  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [filterUnread, setFilterUnread] = useState(2);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const notificationsQuery = useQuery({
    queryKey: noticeNotificationQueryKey(uid, filterUnread),
    queryFn: () => fetchNoticeNotificationAll(uid, filterUnread),
    enabled: Boolean(uid),
    keepPreviousData: true,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    onError: () => antNotif.error({ message: t("notification_page.1") }),
  });

  const { list, noUnread, empty } = useMemo(
    () => deriveFullPageNoticeState(notificationsQuery.data),
    [notificationsQuery.data],
  );

  const invalidateAllNoticeQueriesForUser = useCallback(async () => {
    if (!uid) return;
    await queryClient.invalidateQueries({
      queryKey: ["notice", "notification", "all", uid],
    });
  }, [queryClient, uid]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterUnread, list]);

  const unreadCount = list.filter((n) => n.status === 0).length;
  const totalCount = list.length;

  const filteredNotifications = list;

  const visibleIds = useMemo(
    () => filteredNotifications.map((n) => n.notification_id),
    [filteredNotifications]
  );
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allSelected;

  const selectedUnreadCount = filteredNotifications.filter(
    (n) => selectedIds.has(n.notification_id) && n.status === 0
  ).length;

  const listBlockingSpinner =
    notificationsQuery.isInitialLoading || markAllLoading;

  const updateNotification = async (id, unreadStatus) => {
    const newStatus = unreadStatus ? 0 : 1;
    const flipItem = (n) =>
      n.notification_id === id ? { ...n, status: newStatus } : n;

    const qKey = noticeNotificationQueryKey(uid, filterUnread);
    queryClient.setQueryData(qKey, (old) => {
      if (!old?.notifications) return old;
      return {
        ...old,
        notifications: old.notifications.map(flipItem),
      };
    });

    const response = await apiRequest(
      "notice/notification/read",
      "POST",
      { notification_id: id, unread: unreadStatus },
      true
    );
    if (!response?.success) {
      antNotif.error({ message: t("notification_page.2") });
      await invalidateAllNoticeQueriesForUser();
    }
  };

  const handleMarkAll = async () => {
    setMarkAllLoading(true);
    try {
      const res = await apiRequest(
        "notice/notification/read-all",
        "POST",
        { user_id: uid },
        true
      );
      if (res?.success) {
        await invalidateAllNoticeQueriesForUser();
      } else {
        antNotif.error({ message: t("notification_page.2") });
      }
    } finally {
      setMarkAllLoading(false);
      setSelectedIds(new Set());
    }
  };

  const markSelectedAsRead = async () => {
    const targets = filteredNotifications.filter(
      (n) => selectedIds.has(n.notification_id) && n.status === 0
    );
    await Promise.all(
      targets.map((n) => updateNotification(n.notification_id, false))
    );
    setSelectedIds(new Set());
  };

  const openNotificationTarget = (notificationText) => {
    const targetLink = extractNotificationLink(notificationText);
    if (targetLink) {
      if (targetLink.startsWith("/console/")) {
        history.push(targetLink);
        return;
      }
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = targetLink.startsWith("/")
          ? `${window.location.origin}${targetLink}`
          : targetLink;
      }
      return;
    }
    const dashboardIdText = notificationText?.split(" : ")[0]?.slice(-5, -1);
    if (dashboardIdText) {
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = `/console/Dashboard/${dashboardIdText}?view_type=trends`;
      }
    }
  };

  const onRowActivate = (notif) => {
    openNotificationTarget(notif.notification_Text);
    if (notif.status === 0) {
      updateNotification(notif.notification_id, false);
    }
  };

  const handleFilterUnread = (e) => {
    setFilterUnread(e.target.checked ? 0 : 2);
  };

  return (
    <div className={styles.panelInner}>
      <div className={styles.gscFilterBar}>
        <label className={styles.unreadOnlyToggle}>
          <input
            type="checkbox"
            checked={filterUnread === 0}
            onChange={handleFilterUnread}
          />
          <span>{t("notification_page.4")}</span>
        </label>
        <button
          type="button"
          className={styles.gscIconBtn}
          onClick={() => notificationsQuery.refetch()}
          aria-label="Refresh"
        >
          <img
            src={isDark ? "/assets/refresh.svg" : "/assets/refresh_black.svg"}
            alt=""
          />
        </button>
      </div>

      <div className={styles.gscBulkBar}>
        <Checkbox
          indeterminate={someSelected}
          checked={allSelected}
          disabled={visibleIds.length === 0}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds(new Set(visibleIds));
            } else {
              setSelectedIds(new Set());
            }
          }}
        />
        <div className={styles.gscBulkRight}>
          {selectedUnreadCount > 0 ? (
            <button
              type="button"
              className={styles.markSelectedLink}
              onClick={markSelectedAsRead}
            >
              {t("notification_page.mark_n_read", {
                count: selectedUnreadCount,
              })}
            </button>
          ) : (
            <>
              {totalCount > 0 && (
                <span className={styles.unreadSummary}>
                  {t("notification_page.unread_status_long", {
                    total: totalCount,
                    unread: unreadCount,
                  })}
                </span>
              )}
              {unreadCount > 0 && (
                <>
                  <span className={styles.bulkSep}>·</span>
                  <button
                    type="button"
                    className={styles.markAllInline}
                    onClick={handleMarkAll}
                  >
                    {t("notification_page.mark_all_read_short")}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Spin spinning={listBlockingSpinner}>
        {noUnread && (
          <p className={styles.gscEmptyBanner}>{t("notification_page.7")}</p>
        )}
        {empty && !listBlockingSpinner && !noUnread && (
          <p className={styles.gscEmptyBanner}>{t("notification_page.9")}</p>
        )}
        <div className={styles.gscList}>
          {filteredNotifications.map((notif, index) => {
            const displayText = extractNotificationDisplay(
              notif.notification_Text
            );
            const hasLink = !!extractNotificationLink(notif.notification_Text);
            const unread = notif?.status === 0;
            return (
              <div
                key={notif.notification_id || `n-${index}`}
                className={styles.gscListRow}
              >
                <div
                  className={styles.gscRowCheck}
                  role="presentation"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedIds.has(notif.notification_id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          next.add(notif.notification_id);
                        } else {
                          next.delete(notif.notification_id);
                        }
                        return next;
                      });
                    }}
                  />
                </div>
                <button
                  type="button"
                  className={styles.gscRowMainBtn}
                  onClick={() => onRowActivate(notif)}
                >
                  <div className={styles.gscRowTextBlock}>
                    <div
                      className={`${styles.gscRowTitle} ${
                        unread ? styles.gscRowTitleUnread : ""
                      }`}
                    >
                      {displayText}
                    </div>
                    <div className={styles.gscRowMeta}>
                      {notif.date} · {categoryForTypeId(notif.type_id, t)}
                      {hasLink && (
                        <>
                          {" "}
                          ·{" "}
                          <span className={styles.gscRowViewHint}>
                            {t("notification_page.view_btn")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </Spin>
    </div>
  );
};

const NotificationsPage = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const isDark = props.theme === "dark";

  const params = new URLSearchParams(location.search);
  const defaultTab = params.get("tab") === "alerts" ? "alerts" : "notifications";
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    const tab = next.get("tab") === "alerts" ? "alerts" : "notifications";
    setActiveTab(tab);
  }, [location.search]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    history.replace(`/console/notifications?tab=${key}`);
  };

  const headerSubtitle =
    props.user?.email || props.user?.fullName || props.user?.company || "";

  const items = [
    {
      key: "notifications",
      label: (
        <span className={styles.tabLabel}>
          <BellOutlined style={{ marginRight: 6 }} />
          {t("nav.notifications")}
        </span>
      ),
      children: <NotificationsPanel user={props.user} isDark={isDark} />,
    },
    {
      key: "alerts",
      label: (
        <span className={styles.tabLabel}>
          <AlertOutlined style={{ marginRight: 6 }} />
          {t("nav.alerts")}
        </span>
      ),
      children: (
        <div className={styles.alertsEmbed}>
          <Alerts embedded user={props.user} theme={props.theme} />
        </div>
      ),
    },
  ];

  return (
    <div className={`${styles.page} ${isDark ? styles.pageDark : ""}`}>
      <div className={styles.shell}>
        <header className={styles.shellHeader}>
          <div>
            <h1 className={styles.shellTitle}>
              {t("notification_page.panel_main_title")}
            </h1>
            {headerSubtitle ? (
              <div className={styles.shellSubtitle}>{headerSubtitle}</div>
            ) : null}
          </div>
          <div className={styles.shellHeaderActions}>
            {activeTab === "notifications" && (
              <button
                type="button"
                className={styles.shellHeaderBtn}
                onClick={() => history.push("/settings/preferences")}
              >
                {t("nav.settings")}
              </button>
            )}
          </div>
        </header>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={items}
          className={styles.tabs}
          size="large"
        />
      </div>
    </div>
  );
};

export default NotificationsPage;
